import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';

interface CookiePreferences {
  necessary: boolean;
  preferential: boolean;
  statistical: boolean;
  marketing: boolean;
}

@Component({
  selector: 'app-cookie-consent',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './cookie-consent.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './cookie-consent.scss'
})
export class CookieConsentComponent implements OnInit {
private readonly translate= inject(TranslateService);

  protected showModal = signal<boolean>(false);
  protected showDetails = signal<boolean>(true);
  protected isRtl = signal<boolean>(false);

  protected preferences = signal<CookiePreferences>({
    necessary: true, // Always true
    preferential: false,
    statistical: false,
    marketing: false
  });

  private readonly COOKIE_CONSENT_KEY = 'cookie_consent_given';
  private readonly COOKIE_PREFERENCES_KEY = 'cookie_preferences';
  private readonly COOKIE_EXPIRY_DAYS = 365;



  ngOnInit(): void {
    // Check if consent has already been given
    const consentGiven = this.getCookie(this.COOKIE_CONSENT_KEY);
    if (!consentGiven) {
      this.showModal.set(true);
    } else {
      // Load saved preferences
      this.loadPreferences();
    }
    this.isRtl.set(this.translate.instant('langDirection') === 'rtl');

  }

  protected togglePreference(type: keyof CookiePreferences): void {
    if (type === 'necessary') return; // Cannot disable necessary cookies

    const current = this.preferences();
    this.preferences.set({
      ...current,
      [type]: !current[type]
    });
  }

  protected toggleDetails(): void {
    this.showDetails.update(value => !value);
  }

  protected enableAll(): void {
    this.preferences.set({
      necessary: true,
      preferential: true,
      statistical: true,
      marketing: true
    });
    this.savePreferences();
    this.closeModal();
  }

  protected enableSelection(): void {
    this.savePreferences();
    this.closeModal();
  }

  protected reject(): void {
    // Only keep necessary cookies
    this.preferences.set({
      necessary: true,
      preferential: false,
      statistical: false,
      marketing: false
    });
    this.savePreferences();
    this.closeModal();
  }

  private savePreferences(): void {
    const prefs = this.preferences();
    this.setCookie(this.COOKIE_CONSENT_KEY, 'true', this.COOKIE_EXPIRY_DAYS);
    this.setCookie(this.COOKIE_PREFERENCES_KEY, JSON.stringify(prefs), this.COOKIE_EXPIRY_DAYS);

    // Apply cookie settings
    this.applyCookieSettings(prefs);
  }

  private loadPreferences(): void {
    const savedPrefs = this.getCookie(this.COOKIE_PREFERENCES_KEY);
    if (savedPrefs) {
      try {
        const prefs = JSON.parse(savedPrefs);
        this.preferences.set(prefs);
        this.applyCookieSettings(prefs);
      } catch (e) {
        console.error('Failed to parse cookie preferences', e);
      }
    }
  }

  private applyCookieSettings(prefs: CookiePreferences): void {
    // Here you can implement logic to enable/disable tracking scripts
    // For example, Google Analytics, Facebook Pixel, etc.

    if (prefs.statistical) {
      // Enable Google Analytics or other statistical tools
      console.log('Statistical cookies enabled');
    }

    if (prefs.marketing) {
      // Enable marketing/advertising cookies
      console.log('Marketing cookies enabled');
    }

    if (prefs.preferential) {
      // Enable preferential cookies
      console.log('Preferential cookies enabled');
    }
  }

  private closeModal(): void {
    this.showModal.set(false);
  }

  private setCookie(name: string, value: string, days: number): void {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict`;
  }

  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length);
      }
    }
    return null;
  }
}
